import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useTranslation } from "react-i18next";
import { ExportButtons } from "@/components/ExportButtons";
import { ErrorState } from "@/components/states";
import { toast } from "sonner";
import { AssistantMessage } from "@/components/AssistantMessage";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { RichText } from "@/components/AssistantMessage";
import { useProfileFlags } from "@/hooks/use-profile-flags";
import { cn } from "@/lib/utils";
import {
  predictNextLessons, generatePrep, explainFirst, buildTomorrowSession, logLesson,
  getPrepAccuracy, type PrepDoc,
} from "@/lib/prep.functions";
import {
  Sparkles, Loader2, ArrowRight, CalendarDays, Info, CheckCircle2, XCircle,
  BookOpen, Clock, Lightbulb, RefreshCw, Bell, HelpCircle,
} from "lucide-react";
import { PageHeader } from "@/components/PageHeader";

export const Route = createFileRoute("/_app/prep")({
  head: () => ({
    meta: [
      { title: "Preparami per le prossime lezioni — Lumen" },
      {
        name: "description",
        content:
          "Lumen stima l'argomento più probabile della prossima lezione e ti prepara in pochi minuti con concetti chiave, parole e domande guida.",
      },
      { name: "robots", content: "noindex,follow" },
    ],
  }),
  component: PrepPage,
});

type Prediction = {
  id: string;
  subject_name: string;
  lesson_date: string;
  last_topic: string | null;
  predicted_topic: string;
  alternatives: string[] | null;
  confidence: string;
  rationale: string | null;
  prep_minutes: number;
  chain: string[] | null;
  prep: PrepDoc | null;
  explainer: string | null;
  actual_topic: string | null;
  accuracy: string | null;
  curriculum_id: string | null;
  topic_id: string | null;
};

function nextSchoolDateISO() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
}

function ConfidenceBadge({ value }: { value: string }) {
  const { t } = useTranslation();
  const tone =
    value === "very_likely" ? "pastel-sage" : value === "likely" ? "pastel-sky" : "pastel-peach";
  return (
    <span className={cn("rounded-full px-2.5 py-1 text-xs font-semibold text-foreground", tone)}>
      {t(`prep.confidence.${value}`, { defaultValue: value })}
    </span>
  );
}

/* ============================ PREP SHEET ============================ */

function PrepSheet({
  prediction, open, onOpenChange, defaultMinutes,
}: {
  prediction: Prediction | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  defaultMinutes: number;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const flags = useProfileFlags();
  const genPrep = useServerFn(generatePrep);
  const explain = useServerFn(explainFirst);
  const [minutes, setMinutes] = useState(defaultMinutes);
  const [prep, setPrep] = useState<PrepDoc | null>(prediction?.prep ?? null);
  const [explainer, setExplainer] = useState<string | null>(prediction?.explainer ?? null);
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const printRef = useRef<HTMLDivElement>(null);

  const create = useMutation({
    mutationFn: (regenerate: boolean) =>
      genPrep({ data: { predictionId: prediction!.id, minutes, regenerate } }),
    onSuccess: (r) => {
      setPrep(r.prep);
      setAnswers({});
      qc.invalidateQueries({ queryKey: ["prep-predictions"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const explainM = useMutation({
    mutationFn: () => explain({ data: { predictionId: prediction!.id } }),
    onSuccess: (r) => setExplainer(r.text),
    onError: (e: Error) => toast.error(e.message),
  });

  if (!prediction) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[92vh] overflow-y-auto rounded-t-3xl pb-[max(env(safe-area-inset-bottom),1rem)]">
        <SheetHeader className="text-left">
          <SheetTitle className="text-base">
            {prediction.subject_name} — {t("prep.prep.title")}
          </SheetTitle>
        </SheetHeader>

        {prep && (
          <ExportButtons
            className="mt-2"
            targetRef={printRef}
            fileName={`${prediction.subject_name}-${prediction.predicted_topic}`}
          />
        )}

        <div ref={printRef} className="bg-background">
        <p className="mt-1 text-xs text-muted-foreground">{t("prep.phrasing")}</p>
        <h3 className="text-lg font-semibold tracking-tight">
          <RichText content={prediction.predicted_topic} />
        </h3>

        {!prep && (
          <div className="mt-4 space-y-3">
            <p className="text-xs font-medium text-muted-foreground">{t("prep.prep.duration")}</p>
            <div className="flex flex-wrap gap-2">
              {[5, 10, 15, 20].map((m) => (
                <Button
                  key={m}
                  size="sm"
                  variant={minutes === m ? "default" : "outline"}
                  onClick={() => setMinutes(m)}
                  className="rounded-full"
                >
                  {t("prep.session.minutes", { n: m })}
                </Button>
              ))}
            </div>
            <Button className="w-full rounded-2xl" onClick={() => create.mutate(false)} disabled={create.isPending}>
              {create.isPending ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t("prep.prep.generating")}</>
              ) : (
                <><Sparkles className="mr-2 h-4 w-4" />{t("prep.prep.generate")}</>
              )}
            </Button>
          </div>
        )}

        {prep && (
          <div className="mt-4 space-y-4">
            <Card className="card-soft border-0 p-4">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                1. {t("prep.prep.first")}
              </h4>
              <p className="mt-1.5 text-sm leading-relaxed"><RichText content={prep.first} /></p>
            </Card>

            <Card className="card-soft border-0 p-4">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                2. {t("prep.prep.three")}
              </h4>
              <ol className="mt-2 space-y-2">
                {prep.threeThings.map((x, i) => (
                  <li key={i} className="flex gap-2 text-sm">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                      {i + 1}
                    </span>
                    <span><RichText content={x} /></span>
                  </li>
                ))}
              </ol>
            </Card>

            <Card className="card-soft border-0 p-4">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                3. {t("prep.prep.keywords")}
              </h4>
              <div className="mt-2 space-y-1.5">
                {prep.keywords.map((k, i) => (
                  <div key={i} className="rounded-xl bg-muted/50 px-3 py-2 text-sm">
                    <b>{k.word}</b>
                    <span className="text-muted-foreground"> — {k.meaning}</span>
                  </div>
                ))}
              </div>
            </Card>

            <Card className="card-soft border-0 p-4">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                4. {t("prep.prep.chain")}
              </h4>
              <div className="mt-3 space-y-1">
                {prep.chain.map((step, i) => (
                  <div key={i}>
                    <div
                      className={cn(
                        "rounded-xl px-3 py-2 text-sm font-medium",
                        i === prep.chain.length - 1 ? "bg-primary/10 text-primary" : "bg-muted/60",
                      )}
                    >
                      {step}
                    </div>
                    {i < prep.chain.length - 1 && (
                      <div className="py-0.5 text-center text-xs text-muted-foreground">↓</div>
                    )}
                  </div>
                ))}
              </div>
              {prediction.topic_id && prediction.curriculum_id && (
                <Link
                  to="/programs/$curriculumId/$topicId"
                  params={{ curriculumId: prediction.curriculum_id, topicId: prediction.topic_id }}
                  className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-primary"
                >
                  <BookOpen className="h-3.5 w-3.5" /> {t("prep.prep.inProgram")}
                </Link>
              )}
            </Card>

            <Card className="pastel-sky border-0 p-4">
              <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide">
                <HelpCircle className="h-3.5 w-3.5" /> 5. {t("prep.prep.question")}
              </h4>
              <p className="mt-1.5 text-sm font-medium"><RichText content={prep.guidingQuestion} /></p>
              <p className="mt-1 text-xs opacity-80">{t("prep.prep.questionHint")}</p>
            </Card>

            <Card className="card-soft border-0 p-4">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t("prep.prep.quiz")}
              </h4>
              <p className="mt-1 text-xs text-muted-foreground">{t("prep.prep.quizHint")}</p>
              <div className="mt-3 space-y-4">
                {prep.quiz.map((q, qi) => {
                  const picked = answers[qi];
                  return (
                    <div key={qi}>
                      <p className="text-sm font-medium"><RichText content={q.question} /></p>
                      <div className="mt-2 space-y-1.5">
                        {q.options.map((opt, oi) => {
                          const isPicked = picked === oi;
                          const isCorrect = oi === q.correct;
                          const answered = picked !== undefined;
                          return (
                            <button
                              key={oi}
                              onClick={() => setAnswers((a) => ({ ...a, [qi]: oi }))}
                              disabled={answered}
                              className={cn(
                                "w-full rounded-xl px-3 py-2 text-left text-sm transition-colors",
                                !answered && "bg-muted/50 hover:bg-muted",
                                answered && isCorrect && "bg-emerald-500/15 font-medium",
                                answered && isPicked && !isCorrect && "bg-destructive/10",
                                answered && !isPicked && !isCorrect && "bg-muted/30 opacity-60",
                              )}
                            >
                              <RichText content={opt} />
                            </button>
                          );
                        })}
                      </div>
                      {picked !== undefined && (
                        <p className="mt-1.5 flex items-start gap-1.5 text-xs text-muted-foreground">
                          {picked === q.correct ? (
                            <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
                          ) : (
                            <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive" />
                          )}
                          <span><RichText content={q.why} /></span>
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>

            <Card className="pastel-sage border-0 p-4">
              <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide">
                <Lightbulb className="h-3.5 w-3.5" /> {t("prep.prep.tipTitle")}
              </h4>
              <p className="mt-1.5 text-sm">
                <RichText content={prep.tip || t("prep.prep.tipFallback")} />
              </p>
            </Card>

            <Button variant="ghost" size="sm" className="w-full" onClick={() => create.mutate(true)} disabled={create.isPending}>
              <RefreshCw className={cn("mr-2 h-3.5 w-3.5", create.isPending && "animate-spin")} />
              {t("prep.prep.regenerate")}
            </Button>
          </div>
        )}

        <div className="mt-5 border-t pt-4">
          {!explainer ? (
            <Button variant="outline" className="w-full rounded-2xl" onClick={() => explainM.mutate()} disabled={explainM.isPending}>
              {explainM.isPending ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t("prep.prep.explaining")}</>
              ) : (
                <><BookOpen className="mr-2 h-4 w-4" />{t("prep.prep.explain")}</>
              )}
            </Button>
          ) : (
            <Card className="card-soft border-0 p-4">
              <p className="mb-2 text-xs text-muted-foreground">{t("prep.prep.explainHint")}</p>
              <AssistantMessage
                content={explainer}
                className={cn("text-sm", flags.dsa && "leading-loose")}
              />
            </Card>
          )}
        </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

/* ============================ PAGE ============================ */

function PrepPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [date, setDate] = useState(nextSchoolDateISO());
  const [openId, setOpenId] = useState<string | null>(null);
  const [sessionMinutes, setSessionMinutes] = useState<number | null>(null);
  const [blockMinutes, setBlockMinutes] = useState<Record<string, number>>({});

  const predict = useServerFn(predictNextLessons);
  const buildSession = useServerFn(buildTomorrowSession);
  const saveLesson = useServerFn(logLesson);
  const accuracyFn = useServerFn(getPrepAccuracy);

  const predictions = useQuery({
    queryKey: ["prep-predictions", date],
    queryFn: async () => predict({ data: { date } }),
  });

  const accuracy = useQuery({ queryKey: ["prep-accuracy"], queryFn: () => accuracyFn({}) });

  const profile = useQuery({
    queryKey: ["prep-profile"],
    queryFn: async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) return null;
      const { data } = await supabase
        .from("profiles")
        .select("prep_notifications,prep_default_minutes")
        .eq("id", u.user.id)
        .maybeSingle();
      return data;
    },
  });

  const refresh = useMutation({
    mutationFn: () => predict({ data: { date, force: true } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["prep-predictions"] }),
    onError: (e: Error) => toast.error(e.message),
  });

  const session = useMutation({
    mutationFn: (total: number | null) =>
      buildSession({ data: { date, ...(total ? { totalMinutes: total } : {}) } }),
    onSuccess: () => setBlockMinutes({}),
    onError: (e: Error) => toast.error(e.message),
  });

  const rows = (predictions.data?.predictions ?? []) as unknown as Prediction[];
  const active = useMemo(() => rows.find((r) => r.id === openId) ?? null, [rows, openId]);
  const totalPrep = rows.reduce((a, r) => a + r.prep_minutes, 0);

  /* lesson log form */
  const [logSubject, setLogSubject] = useState("");
  const [logTopic, setLogTopic] = useState("");
  const [logDate, setLogDate] = useState(new Date().toISOString().slice(0, 10));
  const [logResult, setLogResult] = useState<{ accuracy: string | null; predicted: string | null } | null>(null);

  const logM = useMutation({
    mutationFn: () =>
      saveLesson({ data: { subject: logSubject.trim(), topicText: logTopic.trim(), lessonDate: logDate } }),
    onSuccess: (r) => {
      setLogResult(r);
      setLogTopic("");
      toast.success(t("prep.log.saved"));
      qc.invalidateQueries({ queryKey: ["prep-accuracy"] });
      qc.invalidateQueries({ queryKey: ["prep-predictions"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  async function toggleNotifications(v: boolean) {
    const { data: u } = await supabase.auth.getUser();
    if (!u.user) return;
    await supabase.from("profiles").update({ prep_notifications: v }).eq("id", u.user.id);
    qc.invalidateQueries({ queryKey: ["prep-profile"] });
  }

  return (
    <div className="space-y-5 px-5 pt-8">
      <PageHeader
        tone="sky"
        icon={<Sparkles className="h-5 w-5" />}
        title={t("prep.title")}
        subtitle={t("prep.subtitle")}
      />

      <Card className="card-soft border-0 p-4">
        <div className="flex items-start gap-2.5">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <div>
            <p className="text-sm font-medium">{t("prep.disclaimerTitle")}</p>
            <p className="mt-1 text-xs text-muted-foreground">{t("prep.disclaimer")}</p>
          </div>
        </div>
      </Card>

      <div className="flex items-center gap-2">
        <CalendarDays className="h-4 w-4 text-muted-foreground" />
        <Input
          type="date"
          value={date}
          onChange={(e) => { setDate(e.target.value); session.reset(); }}
          className="h-9 w-auto rounded-xl"
          aria-label={t("prep.nextDay")}
        />
        <Button size="sm" variant="ghost" onClick={() => refresh.mutate()} disabled={refresh.isPending}>
          <RefreshCw className={cn("mr-1.5 h-3.5 w-3.5", refresh.isPending && "animate-spin")} />
          {t("prep.refresh")}
        </Button>
      </div>

      {predictions.isLoading || refresh.isPending ? (
        <Card className="card-soft flex items-center gap-2 border-0 p-5 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> {t("prep.predicting")}
        </Card>
      ) : predictions.isError ? (
        <ErrorState error={predictions.error} onRetry={() => predictions.refetch()} />
      ) : predictions.data?.noTimetable ? (
        <Card className="card-soft border-0 p-5">
          <p className="text-sm text-muted-foreground">{t("prep.noTimetable")}</p>
          <Link to="/planner">
            <Button className="mt-3 rounded-2xl" size="sm">{t("prep.openPlanner")}</Button>
          </Link>
        </Card>
      ) : !rows.length ? (
        <Card className="card-soft border-0 p-5 text-sm text-muted-foreground">{t("prep.session.empty")}</Card>
      ) : (
        <>
          {/* ---- one single session ---- */}
          <Card className="card-soft border-0 p-5">
            <h2 className="text-sm font-semibold">{t("prep.session.title")}</h2>
            <p className="mt-0.5 text-xs text-muted-foreground">{t("prep.session.subtitle")}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {[null, 5, 15, 30].map((m, i) => (
                <Button
                  key={i}
                  size="sm"
                  variant={sessionMinutes === m ? "default" : "outline"}
                  className="rounded-full"
                  onClick={() => setSessionMinutes(m)}
                >
                  {m === null ? `${t("prep.session.auto")} · ${totalPrep} min` : t("prep.session.minutes", { n: m })}
                </Button>
              ))}
            </div>
            <Button
              className="mt-3 w-full rounded-2xl"
              onClick={() => session.mutate(sessionMinutes)}
              disabled={session.isPending}
            >
              {session.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Clock className="mr-2 h-4 w-4" />}
              {t("prep.session.build")}
            </Button>

            {session.data && session.data.blocks.length > 0 && (
              <div className="mt-4 space-y-2">
                <p className="text-xs font-medium text-muted-foreground">
                  {t("prep.session.total")}:{" "}
                  {session.data.blocks.reduce(
                    (a, b) => a + (blockMinutes[b.predictionId] ?? b.minutes),
                    0,
                  )}{" "}
                  min
                </p>
                {session.data.blocks.map((b) => {
                  const mins = blockMinutes[b.predictionId] ?? b.minutes;
                  return (
                    <div key={b.predictionId} className="rounded-2xl bg-muted/50 p-3">
                      <button
                        onClick={() => setOpenId(b.predictionId)}
                        className="flex w-full items-center gap-3 text-left"
                      >
                        <span className="w-14 shrink-0 text-sm font-semibold text-primary">{mins} min</span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm font-medium">{b.subject}</span>
                          <span className="block truncate text-xs text-muted-foreground">{b.topic}</span>
                        </span>
                        <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                      </button>
                      <div className="mt-2 flex flex-wrap items-center gap-1.5">
                        {[5, 10, 15, 20].map((m) => (
                          <Button
                            key={m}
                            size="sm"
                            variant={mins === m ? "default" : "outline"}
                            className="h-7 rounded-full px-2.5 text-xs"
                            onClick={() =>
                              setBlockMinutes((s) => ({ ...s, [b.predictionId]: m }))
                            }
                          >
                            {m}′
                          </Button>
                        ))}
                        <Input
                          type="number"
                          min={3}
                          max={90}
                          value={mins}
                          onChange={(e) =>
                            setBlockMinutes((s) => ({
                              ...s,
                              [b.predictionId]: Math.max(3, Math.min(90, Number(e.target.value) || 3)),
                            }))
                          }
                          className="h-7 w-16 rounded-full text-center text-xs"
                          aria-label={`${b.subject} minuti`}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          {/* ---- per-subject cards ---- */}
          <div className="space-y-3">
            {rows.map((p, i) => (
              <motion.div key={p.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                <Card className="card-soft border-0 p-5">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="text-base font-semibold tracking-tight">{p.subject_name}</h3>
                    <ConfidenceBadge value={p.confidence} />
                  </div>

                  {p.last_topic && (
                    <p className="mt-2 text-xs text-muted-foreground">
                      {t("prep.lastLesson")}: <span className="text-foreground/80">{p.last_topic}</span>
                    </p>
                  )}

                  <p className="mt-3 text-xs uppercase tracking-wide text-muted-foreground">
                    {t("prep.probableTopic")}
                  </p>
                  <p className="text-sm font-medium"><RichText content={p.predicted_topic} /></p>

                  {!!p.alternatives?.length && (
                    <p className="mt-1.5 text-xs text-muted-foreground">
                      {t("prep.alsoPossible")}: {p.alternatives.join(" · ")}
                    </p>
                  )}
                  {p.rationale && (
                    <p className="mt-2 rounded-xl bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
                      <b>{t("prep.why")}:</b> {p.rationale}
                    </p>
                  )}

                  {p.accuracy && (
                    <p className="mt-2 flex items-center gap-1.5 text-xs">
                      {p.accuracy === "correct" ? (
                        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                      ) : (
                        <Info className="h-3.5 w-3.5 text-muted-foreground" />
                      )}
                      {t(`prep.log.accuracy.${p.accuracy}`, { defaultValue: p.accuracy })}
                      {p.actual_topic ? ` — ${p.actual_topic}` : ""}
                    </p>
                  )}

                  <Button className="mt-3 w-full rounded-2xl" onClick={() => setOpenId(p.id)}>
                    <Sparkles className="mr-2 h-4 w-4" />
                    {t("prep.prepareIn", { min: p.prep_minutes })}
                  </Button>
                </Card>
              </motion.div>
            ))}
          </div>
        </>
      )}

      {/* ---- lesson log ---- */}
      <Card className="card-soft border-0 p-5">
        <h2 className="text-sm font-semibold">{t("prep.log.title")}</h2>
        <p className="mt-0.5 text-xs text-muted-foreground">{t("prep.log.subtitle")}</p>
        <div className="mt-3 space-y-2">
          <Input
            value={logSubject}
            onChange={(e) => setLogSubject(e.target.value)}
            placeholder={t("prep.log.subject")}
            className="rounded-xl"
          />
          <Input
            value={logTopic}
            onChange={(e) => setLogTopic(e.target.value)}
            placeholder={t("prep.log.topic")}
            className="rounded-xl"
          />
          <Input type="date" value={logDate} onChange={(e) => setLogDate(e.target.value)} className="rounded-xl" />
          <Button
            className="w-full rounded-2xl"
            variant="outline"
            disabled={!logSubject.trim() || logTopic.trim().length < 2 || logM.isPending}
            onClick={() => logM.mutate()}
          >
            {logM.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {t("prep.log.save")}
          </Button>
        </div>
        {logResult?.predicted && (
          <div className="mt-3 rounded-xl bg-muted/50 px-3 py-2 text-xs">
            <p>{t("prep.log.predicted")}: <b>{logResult.predicted}</b></p>
            {logResult.accuracy && (
              <p className="mt-0.5">
                {t("prep.log.result")}:{" "}
                <b>{t(`prep.log.accuracy.${logResult.accuracy}`, { defaultValue: logResult.accuracy })}</b>
              </p>
            )}
          </div>
        )}
        {!!accuracy.data?.total && (
          <p className="mt-3 text-xs text-muted-foreground">
            {t("prep.log.stats", { correct: accuracy.data.correct, total: accuracy.data.total })}
          </p>
        )}
      </Card>

      {/* ---- notifications ---- */}
      <Card className="card-soft border-0 p-5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="flex items-center gap-1.5 text-sm font-semibold">
              <Bell className="h-4 w-4" /> {t("prep.notifications.title")}
            </h2>
            <p className="mt-0.5 text-xs text-muted-foreground">{t("prep.notifications.body")}</p>
          </div>
          <Switch
            checked={profile.data?.prep_notifications ?? true}
            onCheckedChange={toggleNotifications}
            aria-label={t("prep.notifications.title")}
          />
        </div>
      </Card>

      <PrepSheet
        key={active?.id ?? "none"}
        prediction={active}
        open={!!active}
        onOpenChange={(v) => !v && setOpenId(null)}
        defaultMinutes={Math.min(
          20,
          Math.max(5, (active && blockMinutes[active.id]) || profile.data?.prep_default_minutes || 10),
        )}
      />
    </div>
  );
}